import * as React from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { Text, MonoText } from "./typography";
import { CurrencyValue } from "./value-display";
import { Badge } from "./badge";
import { API_BASE_URL } from '../../lib/api';

/**
 * DeleteIncomeDialog - Confirmation dialog for deleting an income entry
 */
export function DeleteIncomeDialog({
  open,
  onOpenChange,
  onSuccess,
  income,
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset error when dialog opens
  React.useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!income?.incomeId) {
      setError("No income entry selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(
        `${API_BASE_URL}/income/${income.incomeId}`,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to delete income:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!income) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Income
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete this
            income record.
          </DialogDescription>
        </DialogHeader>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive p-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}

        {/* Income Details */}
        <div className="rounded-lg border border-border/50 p-4 space-y-2">
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Name</Text>
            <Text size="sm" className="font-medium">{income.name}</Text>
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Category</Text>
            <Badge variant="outline" size="sm">{income.category}</Badge>
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Amount</Text>
            <CurrencyValue
              value={income.amount}
              currency={income.currency}
              size="sm"
              className="font-medium"
            />
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Frequency</Text>
            <Text size="sm" className="capitalize">{income.frequency}</Text>
          </div>
          {income.date && (
            <div className="flex justify-between items-center">
              <Text variant="secondary" size="sm">Date</Text>
              <MonoText size="sm">{income.date}</MonoText>
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteIncomeDialog;
